import { Box } from '@mui/material';
import { type ReactNode, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import { OttoChatRunner } from '~/components/otto/OttoChatRunner';
import { OttoHistoryPanel } from '~/components/otto/OttoHistoryPanel';
import { OttoPanel } from '~/components/otto/OttoPanel';
import { OttoProvider, useOtto, useOttoModeOrClosed } from '~/components/otto/OttoProvider';
import { useOttoExamplePropertyName } from '~/data/hooks/useOttoExamplePropertyName';
import { type PageContext, usePageContext } from '~/hooks/usePageContext';
import { useFeatureFlags } from '~/utils/featureFlags';
import { ASK_OTTO, canAccess } from '~/utils/role';

function OttoChatRunners() {
  const { state } = useOtto();
  const pageContext = usePageContext();
  const pageContextRef = useRef<PageContext>(pageContext);
  useEffect(() => {
    pageContextRef.current = pageContext;
  }, [pageContext]);
  return (
    <>
      {state.sessionOrder.map((id) => {
        const session = state.sessions[id];
        if (!session) return null;
        return (
          <OttoChatRunner
            key={id}
            sessionId={id}
            initialMessages={session.messages}
            pageContextRef={pageContextRef}
          />
        );
      })}
    </>
  );
}

function ContentCollapser({ children, active }: { children: ReactNode; active: boolean }) {
  // Not `useOtto()`: while `aiChat` is off the provider publishes no context.
  const collapsed = useOttoModeOrClosed() === 'full';
  return (
    <Box
      aria-hidden={collapsed}
      inert={collapsed}
      sx={
        active
          ? {
              flex: collapsed ? '0 0 0' : 1,
              minWidth: 0,
              width: collapsed ? 0 : undefined,
              overflow: collapsed ? 'hidden' : undefined,
              transition: 'flex 220ms ease',
              display: 'flex',
              flexDirection: 'column',
            }
          : { display: 'contents' }
      }
    >
      {children}
    </Box>
  );
}

// Otto is consultant-only, behind two gates that are known at different times.
//
// The role comes from the app-shell loader before the first render and only changes
// with a new login, so a non-consultant never gets the provider at all.
//
// The LaunchDarkly `aiChat` flag can arrive after the shell has mounted: after a
// LaunchDarkly timeout, or in a streaming update. For a consultant the provider and the
// surface wrapper therefore stay in the tree whichever way the flag reads, and the flag
// only changes what renders inside them. Inserting a provider above the shell when the
// flag arrived changed the element type there, and React rebuilt the routed page.
//
// With the flag off there are still no background chat runners, no history panel and no
// snapshot data hook, and the provider publishes no context. Defense-in-depth alongside
// the backend's IsConsultant permission on /api/v1/genui/chat.
function useOttoGates(): { eligible: boolean; enabled: boolean } {
  const flags = useFeatureFlags();
  const { userDetails } = useContext(UserContext);
  const eligible = canAccess(userDetails.role, ASK_OTTO);
  return { eligible, enabled: eligible && flags.aiChat };
}

export function OttoIntegration({ children }: { children: ReactNode }) {
  const { eligible, enabled } = useOttoGates();
  const { data: examplePropertyName } = useOttoExamplePropertyName(enabled);
  const { track } = useAnalytics();
  const location = useLocation();
  // Fires exactly once per logged-in session, the first time the
  // integration mounts with enabled=true. The ref gate makes the event
  // independent of route changes — the location pathname captured is
  // wherever the user first encountered the FAB. `mixpanel.reset()` on
  // logout discards the ref along with the page, so the next login
  // fires again.
  const impressionFiredRef = useRef(false);
  useEffect(() => {
    if (!enabled || impressionFiredRef.current) return;
    impressionFiredRef.current = true;
    track('chat_fab_impression', { route: location.pathname });
  }, [enabled, location.pathname, track]);

  if (!eligible) {
    return <>{children}</>;
  }

  // `children` keeps its slot whether the runners render or not.
  return (
    <OttoProvider examplePropertyName={examplePropertyName} active={enabled}>
      {enabled ? <OttoChatRunners /> : null}
      {children}
    </OttoProvider>
  );
}

export function OttoSurface({ children }: { children: ReactNode }) {
  const { eligible, enabled } = useOttoGates();
  if (!eligible) return <>{children}</>;
  // Same element types whichever way `aiChat` reads, for the reason on `useOttoGates`.
  // With it off, `display: contents` takes both boxes out of layout, so the page lays out
  // as it did with no wrapper.
  //
  // `height: '100%'` here would only resolve against a definite-height ancestor,
  // but the layout establishes height via `minHeight: 100%`, so the percentage
  // doesn't resolve and this box collapses to its content height — leaving the
  // grey page background short on pages with little content (e.g. Tools). Letting
  // flex `align-items: stretch` size it fills the area while still growing taller
  // than the viewport when content needs it.
  return (
    <Box
      sx={
        enabled
          ? { display: 'flex', flex: 1, minWidth: 0, alignSelf: 'stretch' }
          : { display: 'contents' }
      }
    >
      <ContentCollapser active={enabled}>{children}</ContentCollapser>
      {enabled ? (
        <>
          <OttoPanel />
          <OttoHistoryPanel />
        </>
      ) : null}
    </Box>
  );
}
